import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Mascot from '@/components/Mascot';
import GiulioMiniChat from '@/components/GiulioMiniChat';
import { useGiulioQuote } from '@/lib/giulioQuotes';

const STORAGE_KEY = 'agrinexus.giulio.dismissed';
const LABEL_STORAGE_KEY = 'agrinexus.giulio.labelSeen';

/**
 * Giulio il Contadino — assistente AI flottante persistente.
 * Appare in basso a destra in ogni pagina (eccetto admin/auth/chat).
 * Click → va all'assistente AI (login se necessario).
 * Label si auto-nasconde dopo 4s alla prima visita, poi ricompare solo su hover.
 */
export default function AiFloatingButton() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [showLabel, setShowLabel] = useState(() => {
    try { return localStorage.getItem(LABEL_STORAGE_KEY) !== '1'; } catch { return true; }
  });
  const [hover, setHover] = useState(false);

  // Nascondi label dopo 4s + salva in localStorage per non mostrarla più
  useEffect(() => {
    if (!showLabel) return;
    const t = setTimeout(() => {
      setShowLabel(false);
      try { localStorage.setItem(LABEL_STORAGE_KEY, '1'); } catch {}
    }, 4000);
    return () => clearTimeout(t);
  }, [showLabel]);

  const hidden = loc.pathname.startsWith('/admin')
    || loc.pathname.startsWith('/assistente')
    || loc.pathname.startsWith('/messaggi')
    || loc.pathname.startsWith('/login')
    || loc.pathname.startsWith('/registrati')
    || loc.pathname.startsWith('/_mascot');
  const quote = useGiulioQuote();
  if (hidden || dismissed) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    setDismissed(true);
  };
  const toggleChat = () => {
    setChatOpen(o => !o);
    // nascondi label alla prima apertura
    if (showLabel) {
      setShowLabel(false);
      try { localStorage.setItem(LABEL_STORAGE_KEY, '1'); } catch {}
    }
  };

  const labelVisible = (showLabel || hover) && !chatOpen;

  return (
    <>
      <GiulioMiniChat open={chatOpen} onClose={() => setChatOpen(false)}/>
      <div className="fixed bottom-4 right-3 sm:bottom-6 sm:right-6 z-40 pb-[env(safe-area-inset-bottom)] flex items-end gap-2">
        {/* Label a sinistra della mascotte, non la copre */}
        <div
          className={`transition-all duration-300 ${labelVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}`}
        >
          <div className="relative mb-6 bg-foreground text-background text-xs px-3 py-2 rounded-2xl rounded-br-sm shadow-xl max-w-[220px]">
            {quote}
            <div className="absolute -right-1.5 bottom-2 w-3 h-3 bg-foreground rotate-45"/>
          </div>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <button
            onClick={toggleChat}
            aria-label="Chiedi a Giulio, l'assistente AgriNexus"
            data-testid="ai-floating-button"
            className={`block w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-background shadow-[0_10px_30px_rgba(0,0,0,0.25)] border-2 overflow-hidden hover:scale-105 active:scale-95 transition-all ${chatOpen ? 'border-terracotta' : 'border-accent/40'}`}
          >
            <Mascot pose={chatOpen ? 'wave' : 'wave'} size={76} shadow={false} animated interactive={false} className="pointer-events-none"/>
          </button>
          {!chatOpen && (
            <button
              onClick={dismiss}
              aria-label="Nascondi Giulio"
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-background text-foreground border border-border grid place-items-center hover:bg-secondary shadow-md"
            >
              <X size={11}/>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
